// src/components/player/PlayerInfoCard.js - PHASE 4: Reads from modal data prop (no /infocard fetch)
import React, { useState } from 'react';
import { dynastyTheme, dynastyClasses } from '../../services/colorService';
import { formatBattingAvg, formatERA } from '../../services/statFormatUtils';
import { 
  Plus, Minus, ArrowLeftRight, TrendingUp, Activity, Calendar, 
  ChartBar, FileText, DollarSign, Trophy, Award, History, Brain,
  Shield, Flame, Target, AlertCircle, Zap, ThermometerSun,
  BarChart3, PieChart, Hash, Users, Clock, Percent, Star
} from 'lucide-react';
import { WatchListStar } from '../WatchList';


// Import all the child components
import PlayerOverviewTab from './PlayerOverviewTab';
import PlayerGameLogsTab from './PlayerGameLogsTab';
import PlayerCareerTab from './PlayerCareerTab';
import PlayerContractTab from './PlayerContractTab';
import PlayerHistoricalAnalytics from './PlayerHistoricalAnalytics';
import PlayerAdvancedAnalytics from './PlayerAdvancedAnalytics';

// PHASE 9: Cleaned up - only essential props, all data from infocardData or player.league_context
const PlayerInfoCard = ({
  player,           // Canonical structure with ids, info, stats, league_context
  playerId,         // MLB player ID for API calls
  isPitcher,        // Boolean - is this a pitcher?
  leagueId,         // League ID (optional) for league-specific data
  statsSeasonYear,  // Stats season year (e.g. 2025 during offseason when current_season=2026)
  onAddPlayer,      // Action handler
  onDropPlayer,     // Action handler
  onInitiateTrade   // Action handler
}) => {
  const [activeTab, setActiveTab] = useState('overview');

  // PHASE 4: Read stats + analytics from player prop (from /modal endpoint)
  // No separate /infocard fetch needed — modal data is a superset
  const analytics = player?.analytics;
  const infocardData = {
    stats: player?.stats,
    analytics: analytics,
  };

  const infocardLoading = false;
  const infocardError = null;
  
  if (!player) return null;

  const { components, classes } = dynastyTheme;
  
  // MLB headshot URL - ✅ NESTED CANONICAL
  const mlbImageUrl = `https://img.mlbstatic.com/mlb-photos/image/upload/d_people:generic:headshot:67:current.png/w_426,q_auto:best/v1/people/${player.ids?.mlb}/headshot/67/current`;
  
  // PHASE 9: Extract data from infocardData (stats) and player.league_context (contract/pricing)
  const stats = infocardData?.stats?.season || {};
  const rolling = infocardData?.stats?.rolling_14_day || {};
  
  // Build contract info from player.league_context (canonical structure)
  const leagueContext = player?.league_context;
  const contract_info = leagueContext && leagueContext.status === 'owned' ? {
    team_id: leagueContext.team?.team_id || null,
    team_name: leagueContext.team?.team_name || null,
    owner_name: leagueContext.team?.owner_name || null,
    owned_by_user: leagueContext.team?.is_user_team || false,
    salary: leagueContext.financial?.contract_salary || 0,
    contract_years: leagueContext.financial?.contract_years || 0,
    baseline_price: leagueContext.financial?.baseline_price || 0,
    roster_status: leagueContext.roster?.status || 'bench',
    acquisition_date: leagueContext.roster?.acquisition_date || null
  } : null;
  
  // Build pricing data from contract_info
  const pricing = {
    generated_price: contract_info?.baseline_price || 0,
    price: contract_info?.salary || 0,
    market_value: contract_info?.baseline_price || 0
  };
  
  // Build league player data for ownership/actions
  const leagueData = contract_info ? {
    isOwned: true,
    ownedByUser: contract_info.owned_by_user || false,
    team_name: contract_info.team_name || null,
    owner_name: contract_info.owner_name || null,
    salary: contract_info.salary || 0,
    contract_years: contract_info.contract_years || 0,
    roster_status: contract_info.roster_status || 'bench'
  } : { isOwned: false };

  // REMOVED broken getStat() function - backend returns NESTED stats in hitting/pitching objects

  // Calculate performance metrics
  const calculateGrade = () => {
    if (isPitcher) {
      const era = parseFloat(stats.pitching?.era) || 0;
      const ip = stats.pitching?.innings_pitched || 0;
      if (!ip || ip === 0) return { grade: '--', color: classes.text.neutral };
      if (era < 2.50) return { grade: 'A+', color: classes.text.success };
      if (era < 3.00) return { grade: 'A', color: classes.text.success };
      if (era < 3.50) return { grade: 'B+', color: classes.text.info };
      if (era < 4.00) return { grade: 'B', color: classes.text.info };
      if (era < 4.50) return { grade: 'C+', color: classes.text.warning };
      if (era < 5.00) return { grade: 'C', color: classes.text.warning };
      return { grade: 'D', color: classes.text.error };
    } else {
      const ops = parseFloat(stats.hitting?.ops) || 0;
      const ab = stats.hitting?.at_bats || 0;
      if (!ab || ab === 0) return { grade: '--', color: classes.text.neutral };
      if (ops > 0.950) return { grade: 'A+', color: classes.text.success };
      if (ops > 0.850) return { grade: 'A', color: classes.text.success };
      if (ops > 0.800) return { grade: 'B+', color: classes.text.info };
      if (ops > 0.750) return { grade: 'B', color: classes.text.info };
      if (ops > 0.700) return { grade: 'C+', color: classes.text.warning };
      if (ops > 0.650) return { grade: 'C', color: classes.text.warning };
      return { grade: 'D', color: classes.text.error };
    }
  };

  const gradeInfo = calculateGrade();

  // Calculate hot/cold status using analytics if available
  const getHotColdStatus = () => {
    // Use infocardData.analytics.hot_cold if available
    if (infocardData?.analytics?.hot_cold) {
      const hotCold = infocardData.analytics.hot_cold;
      return { 
        status: hotCold.status?.toUpperCase() || 'STEADY', 
        emoji: hotCold.temperature || '➖', 
        color: hotCold.status === 'hot' ? classes.text.error :
               hotCold.status === 'cold' ? classes.text.info :
               classes.text.neutral
      };
    }
    
    // Fallback calculation
    if (!rolling || !Object.keys(rolling).length) return { status: 'STEADY', emoji: '➖', color: classes.text.neutral };
    
    const recentAvg = rolling.hitting?.batting_avg || 0;
    const seasonAvg = stats.hitting?.batting_avg || 0;
    const diff = recentAvg - seasonAvg;
    
    if (isPitcher) {
      const recentEra = rolling.pitching?.era || 0;
      const seasonEra = stats.pitching?.era || 0;
      const eraDiff = seasonEra - recentEra; // Lower is better for ERA
      
      if (eraDiff > 1.00) return { status: 'ON FIRE', emoji: '🔥', color: classes.text.error };
      if (eraDiff > 0.50) return { status: 'HOT', emoji: '📈', color: classes.text.warning };
      if (eraDiff < -1.00) return { status: 'COLD', emoji: '❄️', color: classes.text.info };
      if (eraDiff < -0.50) return { status: 'COOLING', emoji: '📉', color: classes.text.primary };
      return { status: 'STEADY', emoji: '➖', color: classes.text.neutral };
    } else {
      if (diff > 0.050) return { status: 'ON FIRE', emoji: '🔥', color: classes.text.error };
      if (diff > 0.025) return { status: 'HOT', emoji: '📈', color: classes.text.warning };
      if (diff < -0.050) return { status: 'COLD', emoji: '❄️', color: classes.text.info };
      if (diff < -0.025) return { status: 'COOLING', emoji: '📉', color: classes.text.primary };
      return { status: 'STEADY', emoji: '➖', color: classes.text.neutral };
    }
  };

  const hotCold = getHotColdStatus();

  // Format helpers - delegate to shared utils
  const fmt = {
    avg: (val) => formatBattingAvg(val),
    era: (val) => formatERA(val),
    whip: (val) => formatBattingAvg(val),
    ops: (val) => formatBattingAvg(val),
    ip: (val) => (parseFloat(val) || 0).toFixed(1)
  };

  // Build tabs - Contract only in league; Performance killed in Phase 4
  const tabs = [
    { id: 'overview', label: `${statsSeasonYear} Overview`, icon: TrendingUp },
    { id: 'gamelogs', label: 'Game Logs', icon: Calendar },
    { id: 'career', label: 'Career', icon: Award },
    { id: 'historical', label: 'Historical', icon: History },
    { id: 'advanced', label: 'Advanced', icon: ChartBar }
  ];
  
  if (leagueId) {
    tabs.push({ id: 'contract', label: 'Contract', icon: DollarSign });
  }

  return (
    <div className={`${components.card.base} overflow-hidden`}>
      {/* COMPACT HEADER */}
      <div className="p-3 bg-gradient-to-r from-neutral-900/95 to-neutral-850/95">
        <div className="flex gap-3 items-start">
          
          {/* Player Image - ✅ NESTED CANONICAL */}
          <div className="relative flex-shrink-0">
            <img 
              src={mlbImageUrl} 
              alt={`${player.info?.first_name} ${player.info?.last_name}`}
              className={`w-24 h-24 rounded-lg object-cover ${classes.bg.darkFlat} border ${classes.border.primary}`}
            />
            <span className={`absolute -bottom-1 -right-1 ${classes.bg.primary} ${classes.text.black} text-xs font-bold px-1.5 py-0.5 rounded`}>
              #{player.info?.jersey_number || '??'}
            </span>
          </div>

          {/* Main Info - ✅ NESTED CANONICAL */}
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className={`text-xl font-bold ${classes.text.white} flex items-center gap-2`}>
                {player.info?.first_name} {player.info?.last_name}
                <WatchListStar 
                  playerId={player.ids?.mlb} 
                  size={20}
                />
              </h1>
              <span className={`${classes.bg.primaryLight} ${classes.text.primary} px-2 py-0.5 rounded text-xs font-medium`}>
                {player.info?.position}
              </span>
              <span className={`text-xs ${classes.text.white}`}>{player.info?.mlb_team || 'FA'}</span>
              <span className={`text-xs ${classes.text.neutralLight}`}>
                {player.info?.age || '?'}y • {Math.floor((player.info?.height_inches || 72)/12)}'{(player.info?.height_inches || 72)%12}" • {player.info?.weight_pounds || '?'}lb
              </span>
              {/* 9l.3.e: handedness — NO FALLBACKS. Render whichever is present;
                  omit the segment entirely when both are NULL (no "B/T: ?/?" pollution). */}
              {(player.info?.bats || player.info?.throws) && (
                <span className={`text-xs ${classes.text.neutralLight}`}>
                  {player.info?.bats && `Bats: ${player.info.bats}`}
                  {player.info?.bats && player.info?.throws && ' • '}
                  {player.info?.throws && `Throws: ${player.info.throws}`}
                </span>
              )}
            </div>

            {/* MAIN STATS BAR */}
            <div className={`mt-2 flex items-center gap-3`}>
              {isPitcher ? (
                <>
                  <MiniStat label="W-L" value={`${stats.pitching?.wins || 0}-${stats.pitching?.losses || 0}`} />
                  <MiniStat label="ERA" value={fmt.era(stats.pitching?.era || 0)} primary />
                  <MiniStat label="WHIP" value={fmt.whip(stats.pitching?.whip || 0)} />
                  <MiniStat label="K" value={stats.pitching?.strikeouts_pitched || 0} />
                  <MiniStat label="IP" value={fmt.ip(stats.pitching?.innings_pitched || 0)} />
                  <MiniStat label="QS" value={stats.pitching?.quality_starts || 0} />
                  <MiniStat label="SV" value={stats.pitching?.saves || 0} />
                  <MiniStat label="GP" value={stats.pitching?.games_played || 0} />
                </>
              ) : (
                <>
                  <MiniStat label="AVG" value={fmt.avg(stats.hitting?.batting_avg || 0)} primary />
                  <MiniStat label="HR" value={stats.hitting?.home_runs || 0} />
                  <MiniStat label="RBI" value={stats.hitting?.rbi || 0} />
                  <MiniStat label="R" value={stats.hitting?.runs || 0} />
                  <MiniStat label="H" value={stats.hitting?.hits || 0} />
                  <MiniStat label="SB" value={stats.hitting?.stolen_bases || 0} />
                  <MiniStat label="OPS" value={fmt.ops(stats.hitting?.ops || 0)} />
                  <MiniStat label="GP" value={stats.hitting?.games_played || 0} />
                </>
              )}
            </div>
          </div>

          {/* Right Side Tiles */}
          <div className="flex gap-2">
            {/* Performance Grade */}
            <div className={`${classes.bg.darkLighter} rounded-lg px-3 py-2 text-center`}>
              <div className={`text-[10px] ${classes.text.neutralLight} uppercase`}>Grade</div>
              <div className={`text-2xl font-bold ${gradeInfo.color}`}>{gradeInfo.grade}</div>
            </div>

            {/* Hot/Cold Status */}
            <div className={`${classes.bg.darkLighter} rounded-lg px-3 py-2 text-center`}>
              <div className={`text-[10px] ${classes.text.neutralLight} uppercase`}>Status</div>
              <div className="text-2xl">{hotCold.emoji}</div>
              <div className={`text-[10px] ${hotCold.color}`}>{hotCold.status}</div>
            </div>

            {/* League-specific (pricing/actions) */}
            {leagueId && (
              <div className={`${classes.bg.darkLighter} rounded-lg px-3 py-2 text-center`}>
                <div className={`text-[10px] ${classes.text.neutralLight} uppercase`}>Value</div>
                <div className={`text-lg font-bold ${classes.text.primary}`}>
                  ${pricing.price || 'TBD'}
                </div>
                {!leagueData.isOwned ? (
                  <button
                    onClick={() => onAddPlayer(player)}
                    className={`mt-1 px-2 py-0.5 ${classes.bg.success} ${classes.text.white} rounded text-[10px] font-medium`}
                  >
                    Add
                  </button>
                ) : leagueData.ownedByUser ? (
                  <button
                    onClick={() => onDropPlayer(player)}
                    className={`mt-1 px-2 py-0.5 ${classes.bg.error} ${classes.text.white} rounded text-[10px] font-medium`}
                  >
                    Drop
                  </button>
                ) : (
                  <button
                    onClick={() => onInitiateTrade(player)}
                    className={`mt-1 px-2 py-0.5 ${classes.bg.info} ${classes.text.white} rounded text-[10px] font-medium`}
                  >
                    Trade
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ANALYTICS TILES SECTION - 2 ROWS OF 3 (3 rows in league) - PHASE 2B: Direct API */}
      <div className={`grid grid-cols-3 gap-2 p-3 ${classes.bg.darkFlat}`}>
        
        {/* Loading State */}
        {infocardLoading && (
          <div className={`col-span-3 text-center py-8 ${classes.text.neutralLight}`}>
            <div className="text-lg">Loading analytics...</div>
          </div>
        )}
        
        {/* Error State */}
        {infocardError && !infocardLoading && (
          <div className={`col-span-3 text-center py-8 ${classes.text.error}`}>
            <div className="text-lg font-bold">Failed to load analytics</div>
            <div className="text-sm mt-2">{infocardError.message}</div>
          </div>
        )}
        
        {/* Tiles - ONLY show if data loaded successfully */}
        {!infocardLoading && !infocardError && infocardData && (
          <>
            {/* Row 1 */}
            {/* Recent Trend Tile */}
            <AnalyticsTile
              icon={<TrendingUp className="w-3 h-3" />}
              label="14-Day Trend"
              value={isPitcher ? 
                (infocardData.stats?.rolling_14_day?.pitching?.era || 0) : 
                (infocardData.stats?.rolling_14_day?.hitting?.batting_avg || 0)
              }
              format={isPitcher ? fmt.era : fmt.avg}
              comparison={isPitcher ? 
                (infocardData.stats?.season?.pitching?.era || 0) : 
                (infocardData.stats?.season?.hitting?.batting_avg || 0)
              }
              invertComparison={isPitcher}
            />

            {/* Dominance/Power */}
            <AnalyticsTile
              icon={<Zap className="w-3 h-3" />}
              label={isPitcher ? "Dominance" : "Power"}
              value={isPitcher ? 
                (((infocardData.stats?.season?.pitching?.strikeouts_pitched || 0) / 
                  Math.max(infocardData.stats?.season?.pitching?.innings_pitched || 0, 1)) * 9).toFixed(1) :
                (infocardData.stats?.season?.hitting?.home_runs || 0)
              }
              subValue={isPitcher ? 
                `${infocardData.stats?.season?.pitching?.strikeouts_pitched || 0} K` :
                `${fmt.ops(infocardData.stats?.season?.hitting?.ops || 0)} OPS`
              }
              format={isPitcher ? (v) => `${v} K/9` : null}
            />

            {/* Consistency Score */}
            <AnalyticsTile
              icon={<Target className="w-3 h-3" />}
              label="Consistency"
              value={infocardData.analytics?.consistency?.score || 0}
              format={(v) => `${v}%`}
              subValue={infocardData.analytics?.consistency?.grade || '--'}
              tooltip="Game-to-game performance variance"
            />

            {/* Row 2 */}
            {/* MLB Rank — custom two-line compact layout */}
            <div className={`${classes.bg.darkLighter} rounded-lg p-2`}>
              <div className="flex items-center justify-between mb-1">
                <span className={`text-[10px] ${classes.text.neutralLight} flex items-center gap-1`}>
                  <Trophy className="w-3 h-3" />
                  {isPitcher ? 'Pitcher' : 'Hitter'} Rank - MLB
                </span>
                <span className={`text-[10px] ${classes.text.neutral}`}>({statsSeasonYear})</span>
                <span className={`text-[10px] ${classes.text.neutralLight}`}>Pos - {player.info?.position || '?'}</span>
              </div>
              <div className="flex items-baseline justify-between">
                <span className={`text-lg font-bold ${classes.text.white}`}>
                  {analytics?.rankings?.overall?.mlb?.rank != null ? `#${analytics.rankings.overall.mlb.rank}` : 'No data'}
                </span>
                {analytics?.rankings?.overall?.mlb?.total_players ? (
                  <span className={`text-[10px] ${classes.text.neutral}`}>of {analytics.rankings.overall.mlb.total_players}</span>
                ) : null}
              </div>
            </div>

            {/* Efficiency / Power */}
            <AnalyticsTile
              icon={<Shield className="w-3 h-3" />}
              label={isPitcher ? "Efficiency" : "Power"}
              value={isPitcher ?
                (stats?.pitching?.whip || 0) :
                ((stats?.hitting?.home_runs || 0) /
                  Math.max(stats?.hitting?.at_bats || 1, 1))}
              format={isPitcher ? fmt.whip : (v) => formatBattingAvg(v)}
              subValue={isPitcher ?
                "WHIP" :
                (() => {
                  const hrRank = analytics?.rankings?.categories?.position?.category_ranks?.home_runs;
                  const total = analytics?.rankings?.categories?.position?.total_players;
                  const pos = player.info?.position ?? 'UTIL';
                  return hrRank != null ? `#${hrRank} / ${total} ${pos}` : '';
                })()}
            />

            {/* Hit Streak — only hitters (no pitcher streak data in backend) */}
            {!isPitcher && (
              <AnalyticsTile
                icon={<Flame className="w-3 h-3" />}
                label="Hit Streak"
                value={infocardData?.analytics?.streaks?.hit_streak || 0}
                format={(v) => `${v}G`}
                subValue="Consecutive"
              />
            )}
        
            {/* Row 3 - LEAGUE SPECIFIC (only shows in league context) */}
            {leagueId && (
              <>
                {/* League Rank — custom two-line compact layout */}
                <div className={`${classes.bg.darkLighter} rounded-lg p-2`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-[10px] ${classes.text.neutralLight} flex items-center gap-1`}>
                      <Hash className="w-3 h-3" />
                      {isPitcher ? 'Pitcher' : 'Hitter'} Rank - League
                    </span>
                    <span className={`text-[10px] ${classes.text.neutral}`}>({statsSeasonYear})</span>
                    <span className={`text-[10px] ${classes.text.neutralLight}`}>Pos - {player.info?.position || '?'}</span>
                  </div>
                  <div className="flex items-baseline justify-between">
                    <span className={`text-lg font-bold ${classes.text.primary}`}>
                      {analytics?.rankings?.overall?.league?.rank != null ? `#${analytics.rankings.overall.league.rank}` : 'No data'}
                    </span>
                    {analytics?.rankings?.overall?.league?.rank != null && analytics?.rankings?.overall?.league?.total_players ? (
                      <span className={`text-[10px] ${classes.text.neutral}`}>of {analytics.rankings.overall.league.total_players}</span>
                    ) : null}
                  </div>
                </div>

                {/* Standings Impact - Category improvements if added */}
                <AnalyticsTile
                  icon={<Star className="w-3 h-3" />}
                  label="Standings Impact"
                  value={infocardData?.analytics?.roto_impact != null ? infocardData.analytics.roto_impact : '--'}
                  format={(v) => typeof v === 'number' ? (v > 0 ? `+${v}` : v.toString()) : v}
                  subValue={infocardData?.analytics?.roto_impact != null ? "Cat. Gain" : ''}
                  color={infocardData?.analytics?.roto_impact > 0 ? classes.text.success : classes.text.neutral}
                  tooltip="Projected category standings improvement if added to your roster"
                />

                {/* Categories Helped tile removed — no backend data source */}
              </>
            )}
          </>
        )}
      </div>

      {/* TABS SECTION */}
      <div className={`${classes.bg.dark}`}>
        <div className={`flex gap-1 px-3 border-b ${classes.border.neutral}`}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                px-3 py-2 text-xs font-medium flex items-center gap-1.5 transition-all
                ${activeTab === tab.id 
                  ? `${classes.text.primary} border-b-2 ${classes.border.primary}` 
                  : `${classes.text.neutralLight}`}
              `}
            >
              <tab.icon className="w-3 h-3" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* TAB CONTENT - Pass data with BACKEND field names */}
        <div className="p-4">
          {activeTab === 'overview' && (
            <PlayerOverviewTab
              player={player}
              playerId={playerId}
              leagueId={leagueId}
              isPitcher={isPitcher}
              statsSeasonYear={statsSeasonYear}
            />
          )}
          
          {activeTab === 'gamelogs' && (
            <PlayerGameLogsTab 
              isPitcher={isPitcher}
              playerId={playerId}
              leagueId={leagueId}
            />
          )}
          
          {activeTab === 'career' && (
            <PlayerCareerTab
              player={player}
              isPitcher={isPitcher}
            />
          )}
          
          {/* Performance tab — KILLED in Phase 4 Step 5 */}
          
          {activeTab === 'historical' && (
            <PlayerHistoricalAnalytics
              player={player}
              playerId={playerId}
              leagueId={leagueId}
              playerName={`${player.info?.first_name} ${player.info?.last_name}`}
              isPitcher={isPitcher}
            />
          )}
          
          {activeTab === 'advanced' && (
            <PlayerAdvancedAnalytics
              playerId={playerId || player.ids?.mlb}
              leagueId={leagueId}
              playerName={`${player.info?.first_name} ${player.info?.last_name}`}
              isPitcher={isPitcher}
              teamId={player?.league_context?.team?.is_user_team ? player.league_context.team.team_id : null}
            />
          )}
          
          {activeTab === 'contract' && leagueId && (
            <PlayerContractTab
              player={player}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// MINI STAT COMPONENT
const MiniStat = ({ label, value, primary }) => {
  const { classes } = dynastyTheme;
  return (
    <div className="flex items-center gap-1">
      <span className={`text-[10px] ${classes.text.neutralLight}`}>{label}:</span>
      <span className={`text-xs font-bold ${primary ? classes.text.primary : classes.text.white}`}>{value}</span>
    </div>
  );
};

// ANALYTICS TILE COMPONENT
const AnalyticsTile = ({ icon, label, value, format, subValue, comparison, invertComparison, color, tooltip }) => {
  const { classes } = dynastyTheme;
  
  const formattedValue = format ? format(value) : value;
  
  let trend = null;
  if (comparison !== undefined) {
    const diff = invertComparison ? comparison - value : value - comparison;
    if (diff > 0) trend = '↑';
    if (diff < 0) trend = '↓';
  }
  
  return (
    <div className={`${classes.bg.darkLighter} rounded-lg p-2`} title={tooltip}>
      <div className={`flex items-center gap-1 mb-1`}>
        <span className={color || classes.text.primary}>{icon}</span>
        <span className={`text-[10px] ${classes.text.neutralLight}`}>{label}</span>
      </div>
      <div className={`text-lg font-bold ${color || classes.text.white} flex items-center gap-1`}>
        {formattedValue}
        {trend && <span className={`text-xs ${trend === '↑' ? classes.text.success : classes.text.error}`}>{trend}</span>}
      </div>
      {subValue && (
        <div className={`text-[10px] ${classes.text.neutral}`}>{subValue}</div>
      )}
    </div>
  );
};

// HELPER COMPONENTS
const StatRow = ({ label, value, primary, large }) => {
  const { classes } = dynastyTheme;
  return (
    <div className="flex justify-between items-center">
      <span className={`${large ? 'text-xs' : 'text-[10px]'} ${classes.text.neutralLight}`}>{label}</span>
      <span className={`${large ? 'text-sm' : 'text-xs'} font-bold ${primary ? classes.text.primary : classes.text.white}`}>{value}</span>
    </div>
  );
};

const SplitTile = ({ label, value }) => {
  const { classes } = dynastyTheme;

  return (
    <div className={`${classes.bg.darkLighter} rounded p-2 text-center`}>
      <div className={`text-[10px] ${classes.text.neutralLight}`}>{label}</div>
      <div className={`text-sm font-bold ${classes.text.white}`}>{formatBattingAvg(value)}</div>
    </div>
  );
};

export default PlayerInfoCard;